import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { ApplicationSettingsModel } from '../../../../application-settings.model';
import { IApplicationSettingsTypeGroupLabels } from '../../../../role';
import { AccessRightType, ApplicationSettingsTypeGroup } from '@fi-shared/types';
import { RoleHelperService } from '../../../../services/role-helper.service';
import { AppTextService } from '@fi-shared/app-text';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { RoleApplicationSettingEditDialogComponent } from './role-application-setting-edit-dialog/role-application-setting-edit-dialog.component';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';

@Component({
    selector: 'fi-role-application-setting',
    templateUrl: './role-application-setting.component.html',
    styleUrls: ['./role-application-setting.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-role-application-setting' },
    standalone: false,
})
export class RoleApplicationSettingComponent implements OnInit {
    @Input() roleApplicationSettingGroups: {
        [key: string]: ApplicationSettingsModel[];
    };
    @Input() isEditMode: boolean;
    @Input() key: string;
    @Input() tooltipText: string;

    isEditIconVisible: boolean = false;

    constructor(
        private roleHelperService: RoleHelperService,
        private appTextService: AppTextService,
        private fiDialogsService: FiDialogsService,
        private cdr: ChangeDetectorRef,
        private saveChangesWarningService: SaveChangesWarningService,
    ) {}

    ngOnInit(): void {
        this.isApplicationSettingsChecked();
    }

    private isApplicationSettingsChecked() {
        const isModuleAvailable = this.isModuleRightChecked(this.roleApplicationSettingGroups[this.key]);

        this.roleApplicationSettingGroups[this.key].forEach((applicationSetting: ApplicationSettingsModel) => {
            if (!isModuleAvailable) {
                applicationSetting.AccessRightType = AccessRightType.UnAvailable;
            }
        });
    }

    setEditIconVisibility(isVisible: boolean) {
        this.isEditIconVisible = isVisible;
    }

    getCardBorderColorClass(applicationsSettings: ApplicationSettingsModel[]): string {
        const accessRightType: AccessRightType = Math.max(
            ...applicationsSettings.map(
                (applicationsSetting: ApplicationSettingsModel) => applicationsSetting.AccessRightType,
            ),
        );
        return this.roleHelperService.getApplicationSettingColor(accessRightType);
    }

    isModuleRightChecked(applicationSettings: ApplicationSettingsModel[]): boolean {
        return this.roleHelperService.isModuleRightChecked(applicationSettings);
    }

    getModuleRightsLabels(applicationsSettings: ApplicationSettingsModel[]): string {
        return applicationsSettings
            .map((applicationsSetting: ApplicationSettingsModel) => {
                const applicationSettingNameTextId = this.getApplicationSettingName(applicationsSetting);
                return `<b>${this.appTextService.get(applicationSettingNameTextId)}</b> ${this.appTextService.get(
                    this.roleHelperService.getAccessLevelName(
                        applicationsSetting.ApplicationSettingType,
                        applicationsSetting.AccessRightType,
                    ),
                )}`;
            })
            .join(' ');
    }

    getGroupLabels(applicationSetting: ApplicationSettingsModel): IApplicationSettingsTypeGroupLabels {
        const applicationSettingTypeGroup: ApplicationSettingsTypeGroup =
            this.roleHelperService.getAccessRightTypeGroup(applicationSetting.ApplicationSettingType);
        return this.roleHelperService.getApplicationSettingsTypeGroupLabels(applicationSettingTypeGroup);
    }

    isModuleRightsShown(key: string): boolean {
        return (
            !!this.roleApplicationSettingGroups[key].length &&
            !!this.getGroupLabels(this.roleApplicationSettingGroups[key][0])
        );
    }

    openEditSettingsDialog(applicationSettings: ApplicationSettingsModel[]) {
        if (!this.isEditMode) return;

        this.fiDialogsService
            .custom({
                contentComponentRef: RoleApplicationSettingEditDialogComponent,
                contentComponentData: applicationSettings,
                config: {
                    closeOnNavigation: false,
                },
            })
            .onYesButtonClick((result: ApplicationSettingsModel[]) => this.updateApplicationSettings(result))
            .afterClosed(() => (this.saveChangesWarningService.closeDialogOnTransition = false));
        this.saveChangesWarningService.closeDialogOnTransition = true;
    }

    private updateApplicationSettings(newApplicationSettingModel: ApplicationSettingsModel[]) {
        this.roleApplicationSettingGroups[this.key].forEach(
            (applicationSetting: ApplicationSettingsModel, index: number) => {
                applicationSetting.ApplicationSettingType = newApplicationSettingModel[index].ApplicationSettingType;
                applicationSetting.AccessRightType = newApplicationSettingModel[index].AccessRightType;
            },
        );
        this.roleHelperService.setRoleDataChanged(true);
        this.cdr.detectChanges();
    }

    private getApplicationSettingName(applicationSetting: ApplicationSettingsModel): string {
        return this.roleHelperService.getApplicationSettingName(applicationSetting.ApplicationSettingType);
    }
}
